import { disableScroll, enableScroll } from "../_helpers";

import gsap from "gsap";

class DesktopMenu {
  private readonly topLevelMenuItems = [...document.querySelectorAll<HTMLElement>("#default-navigation > ul > li")];
  private readonly blackout = document.querySelector<HTMLElement>("#blackout") as HTMLElement;
  private timeout: ReturnType<typeof setTimeout> | null = null;
  private isMenuHovered = false;
  private state: {
    currentTopLevel?: HTMLElement | null;
  } = {
    currentTopLevel: null,
  };

  constructor(private readonly menuRoot: HTMLElement) {}

  init = async () => {
    await this.defineCurrentTopLevelProxy();
    await this.initTopLevelMenuEvents();
    await this.handleBlackout();
  };

  private defineCurrentTopLevelProxy = async () => {
    const proxy = new Proxy(this.state, {
      set: (target, prop, value) => {
        if (prop === "currentTopLevel") {
          if (this.state.currentTopLevel) this.hideSubmenusAndHovered(this.state.currentTopLevel as HTMLLIElement);
          setTimeout(
            () => {
              target[prop] = value;
              this.handleCurrentTopLevelChange(value);
            },
            this.state.currentTopLevel ? 0 : 300
          );
        }

        return true;
      },
    });

    this.state = proxy;
  };

  private handleCurrentTopLevelChange = async (currentTopLevel: HTMLLIElement) => {
    if (!currentTopLevel) {
      gsap.to(this.blackout, { duration: 0.2, autoAlpha: 0, display: "block" });
      enableScroll();
      return;
    }

    if (currentTopLevel.classList.contains("has-children")) {
      if (!this.isMenuHovered) return;
      disableScroll();
      gsap.to(this.blackout, { duration: 0.2, autoAlpha: 1, display: "block" });
      if (!currentTopLevel?.classList.contains("submenus-initialized")) {
        await this.initSubmenuEvents(currentTopLevel);
      }
    } else {
      gsap.to(this.blackout, { duration: 0.2, autoAlpha: 0, display: "block" });
    }
  };

  private initTopLevelMenuEvents = async () => {
    if (this.topLevelMenuItems.length === 0) return;

    this.topLevelMenuItems.forEach((item) => {
      const bg = item.querySelector<HTMLElement>(".bg");
      const link = item.querySelector<HTMLElement>(".top-level-link");
      const submenu = item.querySelector<HTMLElement>(".submenu-wrapper");

      item.addEventListener("mouseenter", () => {
        let timeOutTime = 0;
        if (!submenu) {
          timeOutTime = 0;
        } else {
          timeOutTime = this.state.currentTopLevel ? 0 : 300;
        }

        this.timeout = setTimeout(() => {
          this.timeout = null;
          this.isMenuHovered = true;
          gsap.to(bg, { duration: 0.1, y: 0 });
          gsap.to(link, { duration: 0.1, color: "#fff", opacity: 1 });

          submenu && gsap.to(submenu, { duration: 0.2, scaleY: 1, opacity: 1, delay: 0.05, transformOrigin: "top" });

          const toAnimate = this.topLevelMenuItems.filter((restItem) => restItem !== item).map((restItem) => restItem.querySelector<HTMLElement>(".top-level-link"));
          toAnimate && gsap.to(toAnimate, { duration: 0.1, opacity: 0.5 });
        }, timeOutTime);
      });

      item.addEventListener("mouseleave", () => {
        if (this.timeout) {
          clearTimeout(this.timeout);
          this.timeout = null;
        }
        this.isMenuHovered = false;
        gsap.to(bg, { duration: 0.1, y: -100 });
        gsap.to(link, { duration: 0.1, color: "#000", opacity: 0.5 });
        submenu && gsap.to(submenu, { duration: 0.2, scaleY: 0, opacity: 0, delay: 0.05, transformOrigin: "top" });
      });

      submenu?.addEventListener("click", () => {
        const item = this.state.currentTopLevel;

        if (!item) return;
        const bg = item.querySelector<HTMLElement>(".bg");
        const link = item.querySelector<HTMLElement>(".top-level-link");
        const submenu = item.querySelector<HTMLElement>(".submenu-wrapper");

        gsap.to(bg, { duration: 0.1, y: -100 });
        gsap.to(link, { duration: 0.1, color: "#000", opacity: 0.5 });
        submenu && gsap.to(submenu, { duration: 0.2, scaleY: 0, opacity: 0, delay: 0.05, transformOrigin: "top" });

        this.state.currentTopLevel = null;
      });
    });
  };

  private handleBlackout = async () => {
    this.topLevelMenuItems.forEach((item) => {
      item.addEventListener("mouseenter", () => {
        this.state.currentTopLevel = item.classList.contains("has-children") ? item : null;
      });
    });

    const menuLinksListElement = document.querySelector<HTMLElement>("#default-navigation > ul");

    menuLinksListElement?.addEventListener("mouseleave", () => {
      this.state.currentTopLevel = null;
      gsap.to(
        this.topLevelMenuItems.map((item) => item.querySelector(".top-level-link")),
        { duration: 0.1, opacity: 1 }
      );
    });
  };

  private hideSubmenusAndHovered = async (parent: HTMLLIElement) => {
    const currentHovered: HTMLLIElement[] = [...(parent?.querySelectorAll<HTMLLIElement>(".hover") as NodeList)] as HTMLLIElement[];
    this.handleRemoveHovered(currentHovered, "global");
  };

  private initSubmenuEvents = async (currentTopLevel: HTMLLIElement) => {
    const submenuItems = currentTopLevel.querySelectorAll<HTMLLIElement>(".submenu-list > li");

    submenuItems.forEach((item) => {
      item.addEventListener("mouseenter", () => {
        this.handleSubmenuItemHover(item);
      });
    });

    currentTopLevel?.classList.add("submenus-initialized");
  };

  private handleSubmenuItemHover = async (item: HTMLLIElement) => {
    const currentHovered: HTMLLIElement[] = [...(item.parentElement?.querySelectorAll<HTMLLIElement>(".hover") as NodeList)].filter((hovered) => hovered !== item) as HTMLLIElement[];
    this.handleRemoveHovered(currentHovered, "scoped");

    if (item.parentElement?.parentElement?.classList.contains("radius-removed") && !item.classList.contains("has-children")) {
      gsap.to(item.parentElement?.parentElement as HTMLElement, { duration: 0.1, borderBottomRightRadius: 14 });
      item.parentElement?.parentElement?.classList.remove("radius-removed");
    }

    gsap.to(item, { duration: 0.1, backgroundColor: "#F4F4F4" });
    gsap.to(item.querySelector("a"), { duration: 0.1, textShadow: "1px 0px 0px #000" });

    if (item.classList.contains("has-children")) {
      item.parentElement?.parentElement?.classList.add("radius-removed");
      gsap.to(item.parentElement?.parentElement as HTMLElement, { duration: 0.1, borderBottomRightRadius: 0 });

      const submenu = item.querySelector<HTMLElement>(".submenu");
      submenu?.classList.add("showed");
      gsap.to(submenu, { duration: 0.2, scaleX: "100%", transformOrigin: "left", zIndex: 10, autoAlpha: 1 });
    }

    item.classList.add("hover");
  };

  private handleRemoveHovered = async (currentHovered: Array<HTMLLIElement>, type: "scoped" | "global") => {
    currentHovered.forEach((hovered) => {
      hovered.classList.remove("hover");

      gsap.to(hovered, { duration: 0.1, backgroundColor: "white" });
      gsap.to(hovered.querySelector("a"), { duration: 0.1, textShadow: "0px 0px 0px #000" });

      const showedSubmenus = hovered.querySelectorAll<HTMLElement>(".showed");

      if (showedSubmenus.length > 0) {
        gsap.to(showedSubmenus, { duration: 0.2, scaleX: "0%", transformOrigin: "left", zIndex: 0, autoAlpha: 0 });
        showedSubmenus.forEach((submenu) => submenu.classList.remove("showed"));
      }

      if (type === "scoped") {
        const radiusRemoved = hovered.parentElement?.parentElement?.querySelectorAll<HTMLElement>(".radius-removed");

        if (radiusRemoved && radiusRemoved.length > 0) {
          gsap.to(radiusRemoved, { duration: 0.1, borderBottomRightRadius: 14 });
          radiusRemoved.forEach((item) => item.classList.remove("radius-removed"));
        }
      }
    });

    if (type === "global") {
      const radiusRemoved = this.menuRoot?.querySelectorAll<HTMLElement>(".radius-removed");
      if (radiusRemoved && radiusRemoved.length > 0) {
        gsap.to(radiusRemoved, { duration: 0.1, borderBottomRightRadius: 14 });
        radiusRemoved.forEach((item) => item.classList.remove("radius-removed"));
      }
    }
  };

  public destroy = async () => {
    const initializedSubmenus = this.menuRoot?.querySelectorAll<HTMLElement>(".submenus-initialized");
    initializedSubmenus?.forEach((submenu) => {
      submenu.classList.remove("submenus-initialized");
    });
  };
}

export default DesktopMenu;
